import { useDisableFieldMetadata } from "apps/oneOnboarding/form/useDisableFieldMetadata";
import { useFormController } from "apps/oneOnboarding/form/useFormController";
import { firstAllotmentMapper } from "apps/oneOnboarding/pricing/constants";
import type { FirstCheckInDateControllerProps } from "apps/oneOnboarding/pricing/firstCheckInDate/FirstCheckInDateController";
import { useStaticDataAtomValue } from "apps/oneOnboarding/staticDataAtom";
import type { PropertyDataFirstAllotmentEnum } from "libs/codegen/property-onboarding";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import type { BulkPropertyFormData } from "../BulkPropertyFormData";

export const useBatchFirstCheckInDate = (): FirstCheckInDateControllerProps => {
    const {
        cmsData: {
            pricing: { pleaseChooseDate },
        },
    } = useStaticDataAtomValue();
    const disabled = useDisableFieldMetadata();
    const { control, setValue } = useFormContext<BulkPropertyFormData>();
    const properties = useWatch<BulkPropertyFormData>({
        control,
        name: "propertyDataList",
    }) as BulkPropertyFormData["propertyDataList"];
    const firstAllotmentControl = useFormController<BulkPropertyFormData, "propertyDataList.0.firstAllotment">(
        "propertyDataList.0.firstAllotment",
        {
            defaultValue: firstAllotmentMapper.Immediately,
        },
    );
    const firstAllotmentDateControl = useFormController<BulkPropertyFormData, "propertyDataList.0.firstAllotmentDate">(
        "propertyDataList.0.firstAllotmentDate",
        {
            rules: {
                validate: (value) =>
                    firstAllotmentControl.value !== firstAllotmentMapper.Manual || Boolean(value) || pleaseChooseDate,
            },
        },
    );
    const firstAllotment = firstAllotmentControl.value ?? firstAllotmentMapper.Immediately;

    useEffect(() => {
        properties?.forEach((property, index) => {
            const propertyFirstAllotment = property.firstAllotment ?? firstAllotment;

            if (property.firstAllotment == null) {
                if (firstAllotment === firstAllotmentMapper.Manual && !firstAllotmentDateControl.value) {
                    return;
                }

                setValue(`propertyDataList.${index}.firstAllotment`, firstAllotment, { shouldDirty: false });
                if (firstAllotment === firstAllotmentMapper.Manual) {
                    setValue(`propertyDataList.${index}.firstAllotmentDate`, firstAllotmentDateControl.value, {
                        shouldDirty: false,
                    });
                }
            }

            if (propertyFirstAllotment === firstAllotmentMapper.Immediately && property.firstAllotmentDate !== null) {
                setValue(`propertyDataList.${index}.firstAllotmentDate`, null, { shouldDirty: false });
            }
        });
    }, [firstAllotment, firstAllotmentDateControl.value, properties, setValue]);

    const handleFirstAllotmentChange = (value: PropertyDataFirstAllotmentEnum) => {
        properties?.forEach((_, index) => {
            setValue(`propertyDataList.${index}.firstAllotment`, value, { shouldDirty: true });
            if (value === firstAllotmentMapper.Immediately) {
                setValue(`propertyDataList.${index}.firstAllotmentDate`, null, {
                    shouldDirty: true,
                    shouldValidate: index === 0,
                });
            }
        });
    };

    const handleFirstAllotmentDateChange = (value: string) => {
        properties?.forEach((_, index) => {
            setValue(`propertyDataList.${index}.firstAllotmentDate`, value, {
                shouldDirty: true,
                shouldValidate: index === 0,
            });
        });
    };

    return {
        firstAllotment,
        firstAllotmentDate: firstAllotmentDateControl.value,
        firstAllotmentDateError: firstAllotmentDateControl.fieldState.error?.message,
        firstAllotmentDateRef: firstAllotmentDateControl.ref,
        disabled,
        onFirstAllotmentChange: handleFirstAllotmentChange,
        onFirstAllotmentDateChange: handleFirstAllotmentDateChange,
    };
};
